import { FileSignature } from 'lucide-react';
import type { TicketRecord } from '@/lib/api/tickets.js';
import type { TicketWaiverType } from '@/lib/api/tickets.js';
import { formatDateTime } from '@/lib/format.js';
import { WaiverSignaturePreview } from '@/components/tickets/WaiverSignaturePreview.js';
import { cn } from '@/lib/utils.js';

type WaiverRowProps = {
  ticket: TicketRecord;
  kind: TicketWaiverType;
  title: string;
  signedAt: string | null | undefined;
  signatureUrl: string | null | undefined;
  version: string | null | undefined;
  skipped?: boolean;
  onCollect?: () => void;
  canCollect?: boolean;
};

function WaiverRow({
  ticket,
  kind,
  title,
  signedAt,
  signatureUrl,
  version,
  skipped,
  onCollect,
  canCollect,
}: WaiverRowProps) {
  const isSigned = Boolean(signedAt && signatureUrl);
  const recordPath = `/tickets/${ticket.id}/waiver/${kind}/record`;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {skipped ? (
            <p className="mt-0.5 text-xs font-medium text-amber-800">Skipped (returning customer)</p>
          ) : isSigned ? (
            <p className="mt-0.5 text-xs text-slate-600">
              Signed {formatDateTime(signedAt!)}
              {version ? ` · v${version}` : ''}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-slate-500">Not signed</p>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
            skipped
              ? 'bg-amber-100 text-amber-900'
              : isSigned
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-slate-200 text-slate-600',
          )}
        >
          {skipped ? 'Skipped' : isSigned ? 'Signed' : 'Pending'}
        </span>
      </div>

      {isSigned && signatureUrl ? (
        <WaiverSignaturePreview
          ticketId={ticket.id}
          waiverType={kind}
          signatureUrl={signatureUrl}
          downloadName={`${ticket.ticketNumber}-${kind}-signature`}
        />
      ) : null}

      <div className="mt-2 flex flex-wrap gap-2">
        {isSigned ? (
          <a
            href={recordPath}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#164477]"
          >
            Open signed record
          </a>
        ) : null}
        {!isSigned && !skipped && canCollect && onCollect ? (
          <button
            type="button"
            onClick={onCollect}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Collect signature
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function TicketWaiversSection({
  ticket,
  onCollectPre,
  onCollectPost,
}: {
  ticket: TicketRecord;
  onCollectPre?: () => void;
  onCollectPost?: () => void;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-slate-500" />
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Waivers</h2>
      </div>
      <div className="space-y-3">
        <WaiverRow
          ticket={ticket}
          kind="pre_repair"
          title="Pre-repair waiver"
          signedAt={ticket.preWaiverSignedAt}
          signatureUrl={ticket.preWaiverSignatureUrl}
          version={ticket.preWaiverVersion}
          skipped={Boolean(ticket.preWaiverSkipped)}
          canCollect
          {...(onCollectPre ? { onCollect: onCollectPre } : {})}
        />
        <WaiverRow
          ticket={ticket}
          kind="post_repair"
          title="Post-repair waiver"
          signedAt={ticket.postWaiverSignedAt}
          signatureUrl={ticket.postWaiverSignatureUrl}
          version={ticket.postWaiverVersion}
          canCollect
          {...(onCollectPost ? { onCollect: onCollectPost } : {})}
        />
      </div>
    </section>
  );
}
